/**
 * `2k` — Faturas & lançamentos.
 *
 * A tela concilia UM número que o broker cobrou contra DOIS que a plataforma
 * conhece: o custo que o FinOps mediu, passado pela fórmula do contrato, e os
 * lançamentos manuais do mês. Os lançamentos dividem a aba com a reconciliação
 * porque é para ela fechar que eles existem — separá-los em outra aba obrigaria
 * a ir e voltar no meio de uma conferência.
 *
 * 🔴 **O fluxo de aprovação está EM ABERTO (§9 item 5) e NÃO foi inventado.**
 * O shot desenha dois estados — `EM ABERTO` e `RECONCILIADA` — e nenhum controle
 * de aprovar ou rejeitar. Esta tela portanto **registra** o valor da fatura e
 * não aprova nada: não há botão de aprovação, não há campo de aprovador e não há
 * estado intermediário. `status` é catálogo aberto, então os estados que a iOX
 * definir aparecem sem deploy do frontend — só as AÇÕES terão que ser
 * construídas.
 *
 * 🔒 **Nenhuma soma acontece aqui.** `custoCalculado`, `totalLancamentosManuais`
 * e `diferenca` vêm do servidor. Subtrair na tela pareceria trivial e decidiria,
 * em React, quais lançamentos entram na comparação — que é regra de negócio.
 */
import { useState } from 'react'

import type { Fatura } from '@/api/contracts/fatura'
import {
  useCriarLancamento,
  useFaturas,
  useLancamentos,
  useRegistrarFatura,
} from '@/api/queries/orcamento'
import {
  EsqueletoDeFormulario,
  EsqueletoDeLista,
  EsqueletoDeTabela,
  RegiaoDeCarregamento,
} from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao, Campo, Entrada, Selecao } from '@/components/primitivos'
import { formatarBrl, formatarCompetencia, formatarData } from '@/format'
import { usePermission } from '@/permissions/usePermission'
import { useClienteAtual } from '@/tenant/useClienteAtual'

/**
 * Rótulo e tom de um status de fatura.
 *
 * ⚠️ Catálogo ABERTO: um status desconhecido é mostrado como veio, com tom
 * neutro, e nunca faz a tela quebrar. É o mesmo desenho do `BadgeDeStatus` de
 * FinOps, e existe pela mesma razão — o dia em que a iOX definir o fluxo de
 * aprovação, os estados novos chegam antes do deploy.
 */
const STATUS: Record<string, { rotulo: string; tom: 'atencao' | 'sucesso' }> = {
  em_aberto: { rotulo: 'em aberto', tom: 'atencao' },
  reconciliada: { rotulo: 'reconciliada', tom: 'sucesso' },
}

export function Faturas() {
  const idCliente = useClienteAtual()
  const { editar } = usePermission('orcamento.faturas')

  const consulta = useFaturas(idCliente)
  const [idSelecionada, setIdSelecionada] = useState<string | undefined>()

  const faturas = consulta.data ?? []
  // A fatura em aberto primeiro: é a que precisa de ação. Sem isso a tela abre
  // numa competência já fechada, que não pede nada de ninguém.
  const selecionada =
    faturas.find((f) => f.idFatura === idSelecionada) ??
    faturas.find((f) => f.status === 'em_aberto') ??
    faturas[0]

  if (consulta.isPending) {
    return (
      <RegiaoDeCarregamento
        rotulo="Carregando faturas…"
        className="grid gap-4 xl:grid-cols-[minmax(0,24rem)_1fr]"
      >
        <EsqueletoDeLista itens={5} />
        <EsqueletoDeFormulario linhas={3} />
      </RegiaoDeCarregamento>
    )
  }
  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }
  if (faturas.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma fatura para este cliente."
        descricao="As faturas aparecem por mês fiscal assim que houver um contrato vigente com consumo medido."
      />
    )
  }

  return (
    <section className="grid gap-4 xl:grid-cols-[minmax(0,24rem)_1fr]">
      <div className="border-border-subtle bg-surface-card rounded-card overflow-hidden border">
        <h2 className="border-border-subtle type-h4 text-text-primary border-b p-5">
          Faturas por mês fiscal
        </h2>
        <ul>
          {faturas.map((fatura) => {
            const ehSelecionada = fatura.idFatura === selecionada?.idFatura
            const status = STATUS[fatura.status]
            return (
              <li key={fatura.idFatura}>
                <button
                  type="button"
                  aria-current={ehSelecionada}
                  onClick={() => setIdSelecionada(fatura.idFatura)}
                  className={`border-border-subtle flex w-full items-center justify-between gap-3 border-b px-5 py-4 text-left duration-fast last:border-b-0 ${
                    ehSelecionada ? 'bg-navy-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="text-text-primary type-body font-semibold">
                      {formatarCompetencia(fatura.competencia)}
                    </span>{' '}
                    <span className="text-text-muted type-sm">
                      ({formatarData(fatura.periodoInicio).slice(0, 5)}–
                      {formatarData(fatura.periodoFim).slice(0, 5)})
                    </span>
                  </span>
                  <Badge tom={status?.tom ?? 'neutro'}>{status?.rotulo ?? fatura.status}</Badge>
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      {selecionada && (
        // 🔑 `key` na competência: troca de fatura reinicia os formulários sem
        // `useEffect`. Um valor digitado para junho não pode sobrar em julho.
        <PainelDeReconciliacao
          key={selecionada.idFatura}
          fatura={selecionada}
          idCliente={idCliente}
          editar={editar}
        />
      )}
    </section>
  )
}

function PainelDeReconciliacao({
  fatura,
  idCliente,
  editar,
}: {
  fatura: Fatura
  idCliente: string
  editar: boolean
}) {
  const lancamentos = useLancamentos(idCliente, fatura.competencia)
  const registrar = useRegistrarFatura(idCliente)
  const criarLancamento = useCriarLancamento(idCliente)

  const [valor, setValor] = useState(fatura.valorFatura === null ? '' : String(fatura.valorFatura))
  const [lancando, setLancando] = useState(false)

  const competencia = formatarCompetencia(fatura.competencia)

  return (
    <div className="border-border-subtle bg-surface-card rounded-card border p-5">
      <h3 className="type-h4 text-text-primary">{competencia} — reconciliação da fatura</h3>

      <h4 className="type-label text-text-primary mt-4">Fatura recebida do broker</h4>
      <form
        className="mt-2 flex flex-wrap items-start gap-3"
        onSubmit={(evento) => {
          evento.preventDefault()
          registrar.mutate({
            idFatura: fatura.idFatura,
            valorFatura: Number(valor.replace(',', '.')),
          })
        }}
      >
        <Entrada
          aria-label="Valor da fatura recebida do broker"
          inputMode="decimal"
          placeholder="R$ 0,00"
          // ⚠️ Formatado para quem só lê, cru para quem edita.
          //
          // Quem não pode editar nunca vai digitar aqui, e "127900" num campo
          // desabilitado é um número que o leitor tem que converter de cabeça —
          // encontrado olhando a tela no navegador, não pelos testes. Quem edita
          // recebe o valor cru, porque formatar enquanto se digita briga com o
          // cursor a cada tecla.
          value={
            editar ? valor : fatura.valorFatura === null ? '' : formatarBrl(fatura.valorFatura)
          }
          disabled={!editar}
          onChange={(e) => setValor(e.target.value)}
          className="max-w-md flex-1"
        />
        {editar && (
          <Botao variante="primaria" type="submit" disabled={registrar.isPending}>
            {registrar.isPending ? 'registrando…' : 'registrar fatura'}
          </Botao>
        )}
      </form>

      {registrar.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={registrar.error} />
        </div>
      )}

      <dl className="rounded-card border-border-subtle mt-4 flex flex-col gap-3 border p-4">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-text-body type-sm">Custo calculado (FinOps × fórmula do contrato)</dt>
          <dd className="text-text-primary type-sm font-semibold">
            {formatarBrl(fatura.custoCalculado)}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-text-body type-sm">Lançamentos manuais do mês</dt>
          <dd className="text-text-primary type-sm font-semibold">
            {formatarBrl(fatura.totalLancamentosManuais)}
          </dd>
        </div>
        <div className="border-border-subtle flex items-center justify-between gap-4 border-t pt-3">
          <dt className="text-text-body type-sm">Diferença contra a fatura</dt>
          <dd>
            {/* 🔒 `null` enquanto ninguém registrou a fatura. "R$ 0,00" aqui
                afirmaria que bate exatamente — a mentira mais cara desta tela. */}
            {fatura.diferenca === null ? (
              <span className="text-text-muted type-sm">
                aguardando o valor da fatura do broker
              </span>
            ) : (
              <Badge tom={fatura.diferenca === 0 ? 'sucesso' : 'atencao'}>
                {formatarBrl(fatura.diferenca)}
              </Badge>
            )}
          </dd>
        </div>
      </dl>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h4 className="type-body-lg text-text-primary font-semibold">
          Lançamentos manuais de {competencia}
        </h4>
        {editar && (
          <Botao variante="secundaria" icone="plus" onClick={() => setLancando((l) => !l)}>
            novo lançamento
          </Botao>
        )}
      </div>

      {lancando && (
        <FormularioDeLancamento
          pendente={criarLancamento.isPending}
          erro={criarLancamento.isError ? criarLancamento.error : null}
          aoEnviar={(dados) =>
            criarLancamento.mutate(
              {
                ...dados,
                competencia: fatura.competencia,
                idContrato: fatura.idContrato,
              },
              { onSuccess: () => setLancando(false) },
            )
          }
        />
      )}

      {lancamentos.isPending && (
        <EsqueletoDeTabela
          rotulo="Carregando lançamentos…"
          colunas={4}
          linhas={3}
          comBorda={false}
          className="mt-3"
        />
      )}

      {lancamentos.data && lancamentos.data.length === 0 && (
        <p className="text-text-muted mt-3 type-sm">
          Nenhum lançamento manual neste mês. Consumo de outras nuvens, créditos e ajustes entram
          por aqui — eles nunca são integrados.
        </p>
      )}

      {lancamentos.data && lancamentos.data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-border-subtle border-b">
                {['Descrição', 'Provedor', 'Tipo', 'Valor'].map((rotulo) => (
                  <th
                    key={rotulo}
                    scope="col"
                    className="text-text-muted px-3 py-3 text-left type-eyebrow"
                  >
                    {rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lancamentos.data.map((lancamento) => (
                <tr
                  key={lancamento.idLancamento}
                  className="border-border-subtle border-b last:border-b-0"
                >
                  <td className="text-text-primary px-3 py-3 type-sm">{lancamento.descricao}</td>
                  <td className="text-text-body px-3 py-3 type-sm">{lancamento.provedor}</td>
                  <td className="text-text-body px-3 py-3 type-sm">{lancamento.tipo}</td>
                  <td
                    className={`px-3 py-3 type-sm font-semibold whitespace-nowrap ${
                      lancamento.tipo === 'desconto' ? 'text-success-700' : 'text-text-primary'
                    }`}
                  >
                    {/* O sinal vem do TIPO, e não do valor gravado: um desconto
                        é guardado positivo e mostrado negativo. Confiar no sinal
                        do número faria a mesma linha aparecer diferente
                        conforme quem a cadastrou. */}
                    {lancamento.tipo === 'desconto' ? '− ' : ''}
                    {formatarBrl(Math.abs(lancamento.valor))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔴 §9 item 5 — dito na tela, não escondido no código. */}
      <p className="text-text-muted mt-5 type-sm">
        <i className="ph ph-info text-iox-navy mr-1.5" aria-hidden="true" />
        Registrar a fatura grava o valor cobrado pelo broker. O{' '}
        <strong>fluxo de aprovação da reconciliação</strong> ainda não foi definido pela iOX — por
        isso não existe uma ação de aprovar nesta tela.
      </p>
    </div>
  )
}

/** ⚠️ `provedor` é `<select>` de catálogo — BR-24 proíbe texto livre aqui. */
const PROVEDORES = [
  { valor: 'AWS', rotulo: 'AWS' },
  { valor: 'Azure', rotulo: 'Azure' },
  { valor: 'GCP', rotulo: 'GCP' },
  { valor: 'Ajustes', rotulo: 'Ajustes' },
]

function FormularioDeLancamento({
  pendente,
  erro,
  aoEnviar,
}: {
  pendente: boolean
  erro: unknown
  aoEnviar: (dados: {
    provedor: string
    tipo: 'desconto' | 'acrescimo'
    valor: number
    descricao: string
  }) => void
}) {
  const [descricao, setDescricao] = useState('')
  const [provedor, setProvedor] = useState('')
  const [tipo, setTipo] = useState<'desconto' | 'acrescimo'>('acrescimo')
  const [valor, setValor] = useState('')

  return (
    <form
      className="rounded-card border-border-subtle bg-gray-50 mt-3 grid gap-4 border p-4 md:grid-cols-4"
      onSubmit={(evento) => {
        evento.preventDefault()
        aoEnviar({ provedor, tipo, valor: Number(valor.replace(',', '.')), descricao })
      }}
    >
      <Campo rotulo="Descrição" obrigatorio className="md:col-span-2">
        <Entrada
          required
          placeholder="Contrato Azure — ambiente legado"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
      </Campo>

      <Campo rotulo="Provedor" obrigatorio hint="agrupa o consumo no dashboard">
        {/* ⚠️ Lista fixa por ora: não existe catálogo de provedores na API.
            Registrado no §9 — quando ele existir, esta constante vira query. */}
        <Selecao
          required
          aria-label="Provedor"
          opcoes={PROVEDORES}
          value={provedor}
          onChange={(e) => setProvedor(e.target.value)}
        />
      </Campo>

      <Campo rotulo="Tipo" obrigatorio>
        <Selecao
          required
          aria-label="Tipo"
          placeholder=""
          opcoes={[
            { valor: 'acrescimo', rotulo: 'acréscimo' },
            { valor: 'desconto', rotulo: 'desconto' },
          ]}
          value={tipo}
          onChange={(e) => setTipo(e.target.value as 'desconto' | 'acrescimo')}
        />
      </Campo>

      <Campo rotulo="Valor" obrigatorio hint="sempre positivo — o tipo dá o sinal">
        <Entrada
          required
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
      </Campo>

      <div className="md:col-span-3 flex items-end">
        <Botao variante="primaria" type="submit" disabled={pendente}>
          {pendente ? 'lançando…' : 'salvar lançamento'}
        </Botao>
      </div>

      {erro != null && (
        <div className="md:col-span-4">
          <EstadoDeErro erro={erro} />
        </div>
      )}
    </form>
  )
}
